import React, { useState } from 'react';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Alert,
  Card,
  CardContent,
  Link,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { cleanCpf, formatCpfInput } from '../utils/formatCpf';
import { customerPasswordResetService } from '../services/customerPublicApi';

interface ResetFormValues {
  cpf: string;
  code: string;
  newPassword: string;
  confirmPassword: string;
}

const validationSchema = Yup.object().shape({
  cpf: Yup.string()
    .required('CPF obrigatório')
    .test('cpf-length', 'CPF inválido', (value) => cleanCpf(value || '').length === 11),
  code: Yup.string()
    .required('Código obrigatório')
    .matches(/^\d{6}$/, 'O código deve ter 6 dígitos'),
  newPassword: Yup.string().required('Nova senha obrigatória').min(6, 'Mínimo 6 caracteres'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('newPassword')], 'As senhas não coincidem')
    .required('Confirme a nova senha'),
});

const CustomerResetPassword: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const initialCpfFromState = (location.state as { cpf?: string } | null)?.cpf || '';

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const initialValues: ResetFormValues = {
    cpf: initialCpfFromState ? formatCpfInput(initialCpfFromState) : '',
    code: '',
    newPassword: '',
    confirmPassword: '',
  };

  const handleSubmit = async (values: ResetFormValues) => {
    setSubmitError(null);
    setSubmitSuccess(null);

    try {
      await customerPasswordResetService.confirmReset(
        cleanCpf(values.cpf),
        values.code.replace(/\D/g, ''),
        values.newPassword,
      );
      setSubmitSuccess('Senha redefinida com sucesso! Redirecionando para o login...');
      setTimeout(() => navigate('/customer/login'), 2000);
    } catch (requestError: unknown) {
      const axiosError = requestError as {
        response?: { status?: number; data?: { message?: string } };
      };
      if (axiosError.response?.status === 429) {
        setSubmitError('Muitas tentativas. Aguarde alguns minutos e tente novamente.');
      } else {
        setSubmitError(
          axiosError.response?.data?.message ||
            'Não foi possível redefinir a senha. Verifique o código e tente novamente.',
        );
      }
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Card sx={{ width: '100%', maxWidth: 440 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" component="h1" gutterBottom align="center" fontWeight={700}>
              Redefinir senha
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
              Informe o código recebido e escolha uma nova senha.
            </Typography>

            {submitError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {submitError}
              </Alert>
            )}
            {submitSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {submitSuccess}
              </Alert>
            )}

            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
              enableReinitialize
            >
              {({ errors, touched, values, setFieldValue, isSubmitting }) => (
                <Form>
                  <TextField
                    fullWidth
                    label="CPF"
                    name="cpf"
                    value={values.cpf}
                    onChange={(event) => setFieldValue('cpf', formatCpfInput(event.target.value))}
                    margin="normal"
                    required
                    placeholder="000.000.000-00"
                    inputProps={{ maxLength: 14 }}
                    error={!!(touched.cpf && errors.cpf)}
                    helperText={touched.cpf && errors.cpf}
                    disabled={isSubmitting || !!submitSuccess}
                  />

                  <TextField
                    fullWidth
                    label="Código de 6 dígitos"
                    name="code"
                    value={values.code}
                    onChange={(event) =>
                      setFieldValue('code', event.target.value.replace(/\D/g, '').slice(0, 6))
                    }
                    margin="normal"
                    required
                    inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                    error={!!(touched.code && errors.code)}
                    helperText={touched.code && errors.code}
                    disabled={isSubmitting || !!submitSuccess}
                  />

                  <Field
                    as={TextField}
                    fullWidth
                    label="Nova senha"
                    name="newPassword"
                    type={showPassword ? 'text' : 'password'}
                    margin="normal"
                    required
                    error={!!(touched.newPassword && errors.newPassword)}
                    helperText={touched.newPassword && errors.newPassword}
                    disabled={isSubmitting || !!submitSuccess}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end">
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Field
                    as={TextField}
                    fullWidth
                    label="Confirme a nova senha"
                    name="confirmPassword"
                    type={showConfirm ? 'text' : 'password'}
                    margin="normal"
                    required
                    error={!!(touched.confirmPassword && errors.confirmPassword)}
                    helperText={touched.confirmPassword && errors.confirmPassword}
                    disabled={isSubmitting || !!submitSuccess}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowConfirm((prev) => !prev)} edge="end">
                            {showConfirm ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    sx={{ mt: 3, mb: 2 }}
                    disabled={isSubmitting || !!submitSuccess}
                  >
                    {isSubmitting ? 'Salvando...' : 'Redefinir senha'}
                  </Button>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, textAlign: 'center' }}>
                    <Link component={RouterLink} to="/customer/forgot-password" underline="hover">
                      Solicitar novo código
                    </Link>
                    <Link component={RouterLink} to="/customer/login" underline="hover">
                      Voltar ao login
                    </Link>
                  </Box>
                </Form>
              )}
            </Formik>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default CustomerResetPassword;
